import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Icon from "@ant-design/icons";
import { Button, DatePicker, Input, notification, Spin } from "antd";
import Header from "../../components/layout/header";
import Styles from "./registerTrolleyError.module.scss";
import { LeftIcon, CustomSpin } from "../../assets/icons/icons";
import { api } from "../../services/api";
import dayjs, { Dayjs } from "dayjs";

const RegisterToteError = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toteId = searchParams.get("toteId") || "";

  // Form fields
  const [msn, setMsn] = useState("");
  const [usn, setUsn] = useState("");
  // const [itemCount, setItemCount] = useState<number | string>(1);
  // const [weight, setWeight] = useState(0);
  const [pointOfDelivery, setPointOfDelivery] = useState("");
  const [dateOfDelivery, setDateOfDelivery] = useState<Dayjs | null>(null);
  const [pointOfUse, setPointOfUse] = useState("");
  const [dateOfUse, setDateOfUse] = useState<Dayjs | null>(null);

  // Error states for validation
  const [pointOfDeliveryError, setPointOfDeliveryError] = useState<
    string | null
  >(null);
  const [dateOfDeliveryError, setDateOfDeliveryError] = useState<string | null>(
    null
  );
  const [pointOfUseError, setPointOfUseError] = useState<string | null>(null);
  const [dateOfUseError, setDateOfUseError] = useState<string | null>(null);

  // State for checking if tote exists with "Completed" status
  const [isCheckingTote, setIsCheckingTote] = useState(true);
  const [toteExists, setToteExists] = useState(false);
  const [isCompletedStatus, setIsCompletedStatus] = useState(false);

  // Check if tote exists and pre-fill if it's Completed status
  useEffect(() => {
    const checkToteStatus = async () => {
      if (!toteId) {
        setIsCheckingTote(false);
        return;
      }

      try {
        const response = await api.checkTote({ tote_id: toteId });

        if (
          response.status_code === 200 &&
          response.tote_details &&
          (response.tote_details.tote_id || response.tote_details.id)
        ) {
          // Extract the record ID from checkTote response
          const toteRecordId = response.tote_details.id;
          // Use record ID to fetch tote details, fallback to toteId from URL if record ID not available
          const toteIdToFetch = toteRecordId ? String(toteRecordId) : toteId;

          const toteStatus =
            response.tote_details.tote_status?.toLowerCase() || "";

          // If tote exists and has Completed status, pre-fill the form
          if (toteStatus === "completed") {
            setToteExists(true);
            setIsCompletedStatus(true);

            // Pre-fill form fields from existing tote data
            // Use record ID from checkTote response
            const toteDetails = await api.getToteById(toteIdToFetch);
            setMsn(toteDetails.msn || "");
            setUsn(toteDetails.usn || toteDetails.usm || "");
            // setItemCount(parseInt(toteDetails.itemCount) || 1);
            // setWeight(parseFloat(toteDetails.weight) || 0);
            setPointOfDelivery(toteDetails.pointOfDelivery || "");
            setPointOfUse(toteDetails.pointOfUse || "");
            setDateOfDelivery(
              toteDetails.dateOfDelivery
                ? dayjs(toteDetails.dateOfDelivery)
                : null
            );
            setDateOfUse(
              toteDetails.dateOfUse ? dayjs(toteDetails.dateOfUse) : null
            );
          } else {
            // Tote exists but not in Completed status
            setToteExists(false);
            setIsCompletedStatus(false);
            // Restore form values from sessionStorage if tote doesn't have Completed status
            restoreFormFromStorage();
          }
        } else {
          // Tote doesn't exist
          setToteExists(false);
          setIsCompletedStatus(false);
          // Restore form values from sessionStorage if tote doesn't exist
          restoreFormFromStorage();
        }
      } catch (error) {
        console.error("Failed to check tote status:", error);
        setToteExists(false);
        setIsCompletedStatus(false);
        // Restore form values from sessionStorage on error
        restoreFormFromStorage();
      } finally {
        setIsCheckingTote(false);
      }
    };

    const restoreFormFromStorage = () => {
      const STORAGE_KEY = `registerToteFormData_${toteId}`;
      try {
        const savedData = sessionStorage.getItem(STORAGE_KEY);
        if (savedData) {
          const parsed = JSON.parse(savedData);
          if (parsed.msn) setMsn(parsed.msn);
          if (parsed.usn) setUsn(parsed.usn);
          // if (parsed.itemCount !== undefined) setItemCount(parsed.itemCount);
          // if (parsed.weight !== undefined) setWeight(parsed.weight);
          if (parsed.pointOfDelivery) setPointOfDelivery(parsed.pointOfDelivery);
          if (parsed.pointOfUse) setPointOfUse(parsed.pointOfUse);
          if (parsed.dateOfDelivery) {
            setDateOfDelivery(dayjs(parsed.dateOfDelivery));
          }
          if (parsed.dateOfUse) {
            setDateOfUse(dayjs(parsed.dateOfUse));
          }
        }
      } catch (error) {
        console.warn("Failed to restore form data from sessionStorage", error);
      }
    };

    checkToteStatus();
  }, [toteId]);

  const handleContinue = () => {
    // Clear previous errors
    setPointOfDeliveryError(null);
    setDateOfDeliveryError(null);
    setPointOfUseError(null);
    setDateOfUseError(null);

    // Validate required fields
    let hasError = false;

    if (!msn || !usn) {
      notification.error({
        message: "Missing Information",
        description: "Please fill in MSN and USM fields",
        duration: 3,
      });
      return;
    }

    // Validate new mandatory fields
    if (!pointOfDelivery || !pointOfDelivery.trim()) {
      setPointOfDeliveryError("Point of Delivery is required");
      hasError = true;
    }

    if (!dateOfDelivery) {
      setDateOfDeliveryError("Date of Delivery is required");
      hasError = true;
    }

    if (!pointOfUse || !pointOfUse.trim()) {
      setPointOfUseError("Point of Use / Station is required");
      hasError = true;
    }

    if (!dateOfUse) {
      setDateOfUseError("Date of Use is required");
      hasError = true;
    }

    if (hasError) {
      notification.error({
        message: "Missing Required Fields",
        description:
          "Please fill in all required fields: Point of Delivery, Date of Delivery, Point of Use / Station, and Date of Use",
        duration: 5,
      });
      return;
    }

    // Navigate to review page with form data
    // const itemCountValue =
    //   typeof itemCount === "string" ? parseInt(itemCount) || 1 : itemCount;
    
    // Save form data to sessionStorage before navigating
    const STORAGE_KEY = `registerToteFormData_${toteId}`;
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          msn,
          usn,
          itemCount: 1,
          weight: 1,
          pointOfDelivery: pointOfDelivery.trim(),
          pointOfUse: pointOfUse.trim(),
          dateOfDelivery: dateOfDelivery ? dateOfDelivery.toISOString() : null,
          dateOfUse: dateOfUse ? dateOfUse.toISOString() : null,
        })
      );
    } catch (error) {
      console.warn("Failed to save form data to sessionStorage", error);
    }

    const params = new URLSearchParams({
      toteId: toteId,
      msn: msn,
      usn: usn,
      itemCount: "1",
      weight: "1",
      pointOfDelivery: pointOfDelivery.trim(),
      pointOfUse: pointOfUse.trim(),
      dateOfDelivery: dateOfDelivery ? dateOfDelivery.toISOString() : "",
      dateOfUse: dateOfUse ? dateOfUse.toISOString() : "",
    });
    navigate(`/register-tote-review?${params.toString()}`);
  };

  const handleBack = () => {
    navigate("/operator-landing");
  };

  // const incrementItemCount = () =>
  //   setItemCount((prev) => {
  //     const numValue = typeof prev === "string" ? parseInt(prev) || 1 : prev;
  //     return numValue + 1;
  //   });
  // const decrementItemCount = () =>
  //   setItemCount((prev) => {
  //     const numValue = typeof prev === "string" ? parseInt(prev) || 1 : prev;
  //     return Math.max(1, numValue - 1);
  //   });

  // const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const value = e.target.value;
  //   if (value === "" || /^\d+(\.\d{0,2})?$/.test(value)) {
  //     setWeight(value === "" ? 0 : parseFloat(value));
  //   }
  // };

  if (isCheckingTote) {
    return (
      <div className={Styles.mainContainer}>
        <Header />
        <div className={Styles.contentContainer}>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin indicator={<CustomSpin />} />
            <div style={{ marginTop: "16px", color: "#666" }}>
              Checking tote status...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={Styles.mainContainer}>
      <Header />
      <div className={Styles.contentContainer}>
        <div className={Styles.header}>
          {/* <LeftOutlined  className={Styles.backButton} /> */}
          <Icon component={LeftIcon} onClick={handleBack} />
          <h2 className={Styles.title}>Register Tote</h2>
        </div>

        <div className={Styles.errorSection}>
          {isCompletedStatus ? (
            <>
              <div className={Styles.errorMessage}>
                The Tote (ID: {toteId}) has completed its journey.
              </div>

              <div className={Styles.promptMessage}>
                You can re-register this tote by updating the details below and
                continuing the registration process.
              </div>
            </>
          ) : (
            <>
              <div className={Styles.errorMessage}>
                The Tote (ID: {toteId}) isn't registered in our system.
              </div>

              <div className={Styles.promptMessage}>
                Would you like to create a new tote record using this code?
              </div>
            </>
          )}
        </div>
        <div className={Styles.scrollContainer}>
          <div className={Styles.scrollArea}>
            <div className={Styles.inputSection}>
              <label className={Styles.inputLabel}>Tote ID</label>
              <Input
                readOnly
                value={toteId}
                className={Styles.inputField}
                allowClear
              />
            </div>

            <div className={Styles.inputSection}>
              <label className={Styles.inputLabel}>MSN</label>
              <Input
                placeholder="Enter MSN"
                value={msn}
                onChange={(e) => setMsn(e.target.value)}
                className={Styles.inputField}
                allowClear
                enterKeyHint="done"
                onPressEnter={(e) => e.currentTarget.blur()}
              />
            </div>

            <div className={Styles.inputSection}>
              <label className={Styles.inputLabel}>USM</label>
              <Input
                placeholder="Enter USM"
                value={usn}
                onChange={(e) => setUsn(e.target.value)}
                className={Styles.inputField}
                allowClear
                enterKeyHint="done"
                onPressEnter={(e) => e.currentTarget.blur()}
              />
            </div>

            {/* <div className={Styles.counterRow}>
              <div className={Styles.counterSection}>
                <label className={Styles.inputLabel}>Total Item</label>
                <div className={Styles.counterControl}>
                  <Button
                    className={Styles.counterButton}
                    icon={<MinusOutlined />}
                    onClick={decrementItemCount}
                  />
                  <Input
                    type="number"
                    min={0}
                    value={itemCount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        // Allow clearing the field temporarily
                        setItemCount("");
                      } else {
                        const numValue = parseInt(value);
                        if (!isNaN(numValue) && numValue >= 0) {
                          setItemCount(numValue);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Set default value of 1 when field loses focus if it's empty
                      if (
                        e.target.value === "" ||
                        parseInt(e.target.value) < 1
                      ) {
                        setItemCount(1);
                      }
                    }}
                    className={Styles.counterInput}
                    onKeyDown={(e) => {
                      // Prevent negative numbers and scientific notation
                      if (
                        e.key === "-" ||
                        e.key === "e" ||
                        e.key === "E" ||
                        e.key === "."
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <Button
                    className={Styles.counterButton}
                    icon={<PlusOutlined />}
                    onClick={incrementItemCount}
                  />
                </div>
              </div> 

              <div className={Styles.counterSection}>
                <label className={Styles.inputLabel}>Tote Weight</label>
                <div className={Styles.weightInputWrapper}>
                  <Input
                    type="number"
                    placeholder="0"
                    value={weight || ""}
                    onChange={handleWeightChange}
                    className={Styles.inputField}
                    min={0}
                    step={0.1}
                    allowClear
                  />
                  <span className={Styles.weightUnitSuffix}>kg</span>
                </div>
              </div>
            </div>
            */}
            {/* Point of Delivery */}
            <div className={Styles.inputSection}>
              <label className={Styles.inputLabel}>
                Point of Delivery <span className={Styles.required}>*</span>
              </label>
              <Input
                placeholder="Enter Point of Delivery"
                value={pointOfDelivery}
                onChange={(e) => {
                  setPointOfDelivery(e.target.value);
                  if (pointOfDeliveryError) setPointOfDeliveryError(null);
                }}
                className={`${Styles.inputField} ${
                  pointOfDeliveryError ? Styles.inputError : ""
                }`}
                allowClear
                enterKeyHint="done"
                onPressEnter={(e) => e.currentTarget.blur()}
              />
              {pointOfDeliveryError && (
                <div className={Styles.errorMessageText}>
                  {pointOfDeliveryError}
                </div>
              )}
            </div>

            {/* Date of Delivery */}
            <div className={Styles.inputSection}>
              <label className={Styles.inputLabel}>
                Date of Delivery <span className={Styles.required}>*</span>
              </label>
              <DatePicker
                showTime={{ format: "HH:mm" }}
                format="DD MMM YYYY, HH:mm"
                value={dateOfDelivery}
                onChange={(val) => {
                  setDateOfDelivery(val);
                  if (dateOfDeliveryError) setDateOfDeliveryError(null);
                }}
                className={`${Styles.inputField} ${
                  dateOfDeliveryError ? Styles.inputError : ""
                }`}
                placement="Bottom"
                allowClear
              />
              {dateOfDeliveryError && (
                <div className={Styles.errorMessageText}>
                  {dateOfDeliveryError}
                </div>
              )}
            </div>

            {/* Point of Use / Station */}
            <div className={Styles.inputSection}>
              <label className={Styles.inputLabel}>
                Point of Use / Station{" "}
                <span className={Styles.required}>*</span>
              </label>
              <Input
                placeholder="Enter Point of Use / Station"
                value={pointOfUse}
                onChange={(e) => {
                  setPointOfUse(e.target.value);
                  if (pointOfUseError) setPointOfUseError(null);
                }}
                className={`${Styles.inputField} ${
                  pointOfUseError ? Styles.inputError : ""
                }`}
                allowClear
                enterKeyHint="done"
                onPressEnter={(e) => e.currentTarget.blur()}
              />
              {pointOfUseError && (
                <div className={Styles.errorMessageText}>{pointOfUseError}</div>
              )}
            </div>

            {/* Date of Use */}
            <div className={Styles.inputSection}>
              <label className={Styles.inputLabel}>
                Date of Use <span className={Styles.required}>*</span>
              </label>
              <DatePicker
                showTime={{ format: "HH:mm" }}
                format="DD MMM YYYY, HH:mm"
                value={dateOfUse}
                onChange={(val) => {
                  setDateOfUse(val);
                  if (dateOfUseError) setDateOfUseError(null);
                }}
                className={`${Styles.inputField} ${
                  dateOfUseError ? Styles.inputError : ""
                }`}
                placement="bottom"
                getPopupContainer={() => document.body}
                popupStyle={{ zIndex: 2000 }}
                allowClear
              />
              {dateOfUseError && (
                <div className={Styles.errorMessageText}>{dateOfUseError}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={Styles.fixedButtonWrapper}>
        <Button
          className={Styles.continueButton}
          size="large"
          onClick={handleContinue}
          disabled={
            !msn ||
            !usn ||
            !pointOfDelivery ||
            !pointOfDelivery.trim() ||
            !dateOfDelivery ||
            !pointOfUse ||
            !pointOfUse.trim() ||
            !dateOfUse
          }
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default RegisterToteError;
